import React, { useContext } from "react";
import { useForm } from "react-hook-form";
import {
    createValidationResult,
    ensure,
    FormDialog,
    FormFields,
    FormValidationSummary,
    Stack,
    useNotifications,
    usePerformMultipleActions
} from "sprint-common-web";

import { useOnPremOrdersApi } from "../../../../../common/api/orders-api/useOnPremOrdersApi";
import { OnPremOrderStatus } from "../../../../common/types/OnPremOrderStatus";
import { getAvailableOnPremOrderStatuses } from "../../../common/utils/getAvailableOnPremOrderStatuses";
import { OnPremOrderListingContext } from "../../context/OnPremOrderListingContext";
import { UpdateMultipleOnPremOrdersSummaryList } from "./UpdateMultipleOnPremOrdersSummaryList";
import { UpdateOnPremOrderStatusRequestItem, validateDiscardedItems } from "./UpdateOnPremOrderStatusRequestItem";

interface UpdateMultipleOnPremOrdersStatusDialogProps {
    onClose(): void;
    selectedStatus?: OnPremOrderStatus;
}

export const UpdateMultipleOnPremOrdersStatusDialog: React.VFC<UpdateMultipleOnPremOrdersStatusDialogProps> = ({
    onClose,
    selectedStatus
}: UpdateMultipleOnPremOrdersStatusDialogProps) => {
    const { updateOnPremOrderStatus } = useOnPremOrdersApi();

    const { selectedItems, updateOnPremOrder } = useContext(OnPremOrderListingContext);

    const { success, error: errorNotification } = useNotifications();

    const { formState, handleSubmit } = useForm();

    const isOrderStatusUpdatable = (item: UpdateOnPremOrderStatusRequestItem) => {
        const availableStatuses = getAvailableOnPremOrderStatuses(item.status);

        return selectedStatus ? availableStatuses.includes(selectedStatus) : false;
    };

    const { performAllItems, isActionInProgress, items } = usePerformMultipleActions<
        UpdateOnPremOrderStatusRequestItem
    >({
        isAvailable: isOrderStatusUpdatable,
        selectedItems
    });

    const formHasErrors = createValidationResult([validateDiscardedItems(items, selectedItems.length, selectedStatus)]);
    const areValidOrdersEmpty = !items.length;

    const submit = async () => {
        const results = await sendAllItems();

        if (results.every(({ status }) => status === "fulfilled")) {
            onClose();

            success("Status update complete");
        } else errorNotification("Something went wrong while sending order requests");
    };

    const sendAllItems = async () => {
        ensure(selectedStatus);

        return performAllItems(async (item) => {
            await updateOnPremOrderStatus({ orderId: item.id, status: selectedStatus });

            updateOnPremOrder({ id: item.id, status: selectedStatus });
        });
    };

    return (
        <Stack>
            <FormDialog
                title={"Update order statuses"}
                formState={formState}
                text={"Are you sure you want to start updating listed orders statuses?"}
                isBlocking={isActionInProgress}
                showCloseButton={false}
                submit={{
                    onClick: handleSubmit(submit),
                    disabled: areValidOrdersEmpty || isActionInProgress
                }}
                cancel={{
                    onClick: onClose
                }}
                width={700}
                contentHeight={400}
            >
                <FormFields>
                    <FormValidationSummary validationResult={formHasErrors} />
                </FormFields>
                {!!items.length && <UpdateMultipleOnPremOrdersSummaryList items={items} />}
            </FormDialog>
        </Stack>
    );
};
